import EmailIcon from "@/assets/icons/email.svg"
import FacebookIcon from "@/assets/icons/facebook.svg"
import LogoIcon from "@/assets/icons/lending-logo.svg"
import TelegramIcon from "@/assets/icons/telegram.svg"
import TwitterIcon from "@/assets/icons/twitter.svg"
import { networkAtom } from "@/atoms/settings"
import navbarLinks from "@/constants/navbarLinks"
import useTheme from "@/hooks/useTheme"
import useTrans from "@/hooks/useTrans"
import { useCheckClient } from "@/providers/client"
import { Box, Container, Grid, Link, Stack, Typography } from "@mui/material"
import { BlockchainNetwork } from "@renec-foundation/relend-adapter"
import { useAtomValue } from "jotai"
import Image from "next/image"
import NextLink from "next/link"
import { useSearchParams } from "next/navigation"
import { Fragment, useMemo } from "react"
import MenuDocument from "../header/components/MenuDocument"
import LendingMenuItem from "../header/desktop/MenuItem"
import * as styles from "./styles"

const SOCIAL_NETWORKS = [
  {
    icon: EmailIcon,
    url: "mailto: lend@renec.org",
    dataTestId: "footer-email",
  },
  {
    icon: FacebookIcon,
    url: "https://www.facebook.com/RENECLend",
    dataTestId: "footer-facebook",
  },
  {
    icon: TwitterIcon,
    url: "https://x.com/RelendOfficial",
    dataTestId: "footer-twitter",
  },
  {
    icon: TelegramIcon,
    url: "https://t.me/RENECLendCommunity",
    dataTestId: "footer-telegram",
  },
]

const View = () => {
  const trans = useTrans()
  const { theme } = useTheme()

  const searchParams = useSearchParams()
  const currentSearchParams = new URLSearchParams(Array.from(searchParams.entries()))
  const currentSearchParamsString = currentSearchParams.toString()
  const query = currentSearchParamsString ? `?${currentSearchParamsString}` : ""
  const { isClient } = useCheckClient()

  const network = useAtomValue(networkAtom)
  const isRenecNetwork = network === BlockchainNetwork.RENEC

  const renecMenus = useMemo(
    () => [
      {
        href: `${navbarLinks.dashboard}`,
        label: trans.header.dashboard,
        dataTestId: "footer-menu-dashboard",
      },
      {
        href: `${navbarLinks.referral}`,
        label: trans.header.referral,
        dataTestId: "footer-menu-referral",
      },
      {
        href: `${navbarLinks.liquidations}`,
        label: trans.header.liquidations,
        dataTestId: "footer-menu-liquidations",
      },
      {
        href: `${navbarLinks.staking}`,
        label: trans.header.staking,
        dataTestId: "footer-menu-staking",
      },
    ],
    [trans],
  )

  const solanaMenus = useMemo(
    () => [
      {
        href: `${navbarLinks.dashboard}${query}`,
        label: trans.header.dashboard,
        dataTestId: "menu-dashboard",
      },
    ],
    [query, trans],
  )

  const menus = useMemo(() => (isRenecNetwork ? renecMenus : solanaMenus), [isRenecNetwork, renecMenus, solanaMenus])

  return (
    <Box
      sx={{
        bottom: 0,
        //bgcolor: theme === "light" ? "white" : "blackLighter",
        marginTop: "auto",
      }}
    >
      <Container maxWidth='xl'>
        <Stack gap={3} className='mb-8 mt-4'>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent='space-between'
            className='bg-white rounded-2xl border border-solid border-secondary py-6 px-4 sm:py-4 sm:px-6'
          >
            <Stack direction='row' className='flex-1'>
              <Link
                component={NextLink}
                sx={styles.textLogoStyle}
                href='/dashboard'
                underline='none'
                data-testid='footer-logo'
              >
                <Image src={LogoIcon} alt='relend-icon' width={48} height={48} />
              </Link>

              <Stack
                direction='row'
                spacing={3}
                alignItems='center'
                justifyContent='center'
                className='flex-1 ml-5 sm:ml-40 flex-wrap'
              >
                <Grid
                  container
                  spacing={0}
                  sx={{
                    mx: -1,
                    "& > .MuiGrid-item": {
                      px: 1,
                      pb: { xs: 2, sm: 0 },
                    },
                  }}
                  className='w-full sm:w-fit items-center'
                >
                  {isClient
                    ? menus.map((menu, index) => {
                        return (
                          <Grid item xs={6} sm={"auto"} key={index}>
                            <LendingMenuItem
                              key={menu.dataTestId}
                              href={menu.href}
                              label={menu.label}
                              dataTestId={menu.dataTestId}
                              isActive={false}
                            />
                          </Grid>
                        )
                      })
                    : null}
                  <Grid item xs={6} sm={"auto"}>
                    <MenuDocument prefixMenuId='footer' />
                  </Grid>
                </Grid>
              </Stack>
            </Stack>
            <Stack direction='row' spacing={1} alignItems='center' justifyContent='center' className='mt-2 sm:mt-0'>
              {SOCIAL_NETWORKS.map((social, index) => {
                return (
                  <Fragment key={index}>
                    <Link
                      className='bg-secondary p-2 sm:p-4 rounded-full'
                      underline='none'
                      component={NextLink}
                      href={social.url}
                      target='_blank'
                      data-testid={social.dataTestId}
                    >
                      <Image src={social.icon} alt='twitter-icon' width={16} height={16} />
                    </Link>
                  </Fragment>
                )
              })}
            </Stack>
          </Stack>
          <Typography
            className='text-center'
            sx={{ ...styles.copyrightStyle, ...(theme === "light" ? {} : { color: "white" }) }}
          >
            © 2023, {trans.base.all_rights_reserved}
          </Typography>
        </Stack>
      </Container>
    </Box>
  )
}

export default View
